import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import {
  Upload, Sparkles, Wand2, Loader2, Trash2, CalendarClock, Coffee,
  CheckCircle2, XCircle, Info, ChevronRight, Palette,
} from "lucide-react";

const PALETTES = [
  // Beyourself Signature — cream base
  { id: "cream", label: "Cream", swatch: ["#F5EFE6", "#B08D57"] },
  // Pastels for lifestyle covers
  { id: "coral_pastel", label: "Coral Pastel", swatch: ["#EEBBA8", "#B08D57"] },
  { id: "terra_pastel", label: "Terra Pastel", swatch: ["#D3A58C", "#B08D57"] },
  { id: "verde_pastel", label: "Sage Pastel", swatch: ["#C6D5BB", "#B08D57"] },
  { id: "verde_agua", label: "Verde Agua", swatch: ["#B7D2C8", "#B08D57"] },
  { id: "azul_pastel", label: "Azul Pastel", swatch: ["#C4D4E0", "#B08D57"] },
  { id: "azul_niebla", label: "Azul Niebla", swatch: ["#B4C3D0", "#B08D57"] },
  { id: "rosa_pastel", label: "Rosa Pastel", swatch: ["#F0D6D6", "#B08D57"] },
  { id: "rosa_shell", label: "Rosa Shell", swatch: ["#EEC8C8", "#B08D57"] },
  { id: "lila_pastel", label: "Lila Pastel", swatch: ["#D8C8E2", "#B08D57"] },
  { id: "lila_dust", label: "Lila Dust", swatch: ["#C6B8D2", "#B08D57"] },
  { id: "moka_claro", label: "Moka Claro", swatch: ["#D3C3AC", "#B08D57"] },
  { id: "taupe", label: "Taupe", swatch: ["#C2B39E", "#B08D57"] },
  { id: "taupe_soft", label: "Taupe Suave", swatch: ["#D0C3B0", "#B08D57"] },
  { id: "oat", label: "Oat", swatch: ["#DFD5C3", "#B08D57"] },
  { id: "vanilla", label: "Vanilla", swatch: ["#EEE3CD", "#B08D57"] },
  // Dark option — cinematic
  { id: "ink", label: "Noir Editorial", swatch: ["#110F0C", "#B08D57"] },
  { id: "forest_dark", label: "Forest Dark", swatch: ["#24372C", "#B08D57"] },
  { id: "nude", label: "Nude · Champagne", swatch: ["#E8D5C4", "#B08D57"] },
];

const CATEGORY_LABEL = {
  comida: "Comida", viaje: "Viaje", ejercicio: "Ejercicio", casa: "Casa",
  familia: "Familia", hobby: "Hobby", moda: "Moda", otro: "Otro",
};

const CATEGORY_TINT = {
  comida: "bg-[#F4D9A6] text-[#7A5B1E]",
  viaje: "bg-[#DDE7F0] text-[#3B5470]",
  ejercicio: "bg-[#FBE1DB] text-[#8C3B27]",
  casa: "bg-[#E8EAE0] text-[#4F5B37]",
  familia: "bg-[#F5E5EF] text-[#7B3F6C]",
  hobby: "bg-[#EEE1F5] text-[#5F3F82]",
  moda: "bg-[#F5EEDF] text-[#7A5B1E]",
  otro: "bg-[#E6E2DC] text-[#5C5E5C]",
};

export default function LifestylePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/lifestyle");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Error cargando lifestyle");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/lifestyle/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
      }
      toast.success(`${files.length} foto${files.length > 1 ? "s" : ""} subida${files.length > 1 ? "s" : ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openItem = items.find((it) => it.id === openId);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="lifestyle-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-1">Marca personal</div>
          <h1 className="font-display text-3xl text-[#1A1C1A]">Lifestyle</h1>
          <p className="text-sm text-[#5C5E5C] mt-1 max-w-xl">
            Sube fotos de tu día a día — comida, viajes, ejercicio, casa. La IA les da tu estilo editorial,
            escribe el copy y te sugiere cuándo publicarlas (target: 1 lifestyle cada 4 clínicos).
          </p>
        </div>
        <label className="inline-flex items-center gap-2 bg-[#1A1C1A] hover:bg-[#D97757] text-white px-5 py-3 rounded-lg cursor-pointer transition text-sm font-medium">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Subiendo…" : "Subir fotos"}
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden"
                 disabled={uploading} data-testid="lifestyle-upload-input" />
        </label>
      </div>

      {/* Rebalance action row */}
      {items.length > 0 && (
        <div className="bg-[#FBE9E1] border border-[#D97757]/30 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] tracking-widest uppercase text-[#D97757] mb-0.5">Barrido de calendario</div>
            <p className="text-xs text-[#5C5E5C]">
              Asigna día y hora óptimos a TODAS las fotos pendientes respetando la regla <b>1 lifestyle cada 3 días</b>.
              No agenda automáticamente — solo pre-sugiere para que revises.
            </p>
          </div>
          <RebalanceButton onDone={load} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#A69076] animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E6E2DC] rounded-2xl p-12 text-center">
          <Coffee className="w-8 h-8 text-[#A69076] mx-auto mb-3" />
          <p className="text-sm text-[#5C5E5C]">Aún no hay fotos lifestyle. Sube tus primeras para empezar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((it) => (
            <LifestyleCard key={it.id} item={it} onOpen={() => setOpenId(it.id)} onReload={load} />
          ))}
        </div>
      )}

      {openItem && (
        <LifestyleDetail
          itemId={openItem.id}
          onClose={() => setOpenId(null)}
          onChange={load}
        />
      )}
    </div>
  );
}

function LifestyleCard({ item, onOpen, onReload }) {
  const cat = item.analysis?.category || "otro";
  const status = item.status;
  const canBeDeleted = status !== "published";
  const del = async (e) => {
    e.stopPropagation();
    if (!confirm("¿Eliminar esta foto? Si tenía post agendado se cancela también.")) return;
    try {
      await api.delete(`/lifestyle/${item.id}`);
      toast.success("Eliminada");
      await onReload();
    } catch { toast.error("Error"); }
  };
  return (
    <button
      onClick={onOpen}
      className="group bg-white border border-[#E6E2DC] rounded-xl overflow-hidden hover:shadow-lg hover:border-[#A69076] transition text-left"
      data-testid={`lifestyle-card-${item.id}`}
    >
      <div className="aspect-square bg-[#F4F0EB] relative overflow-hidden">
        {item.image_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
               alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : null}
        <div className="absolute top-2 left-2 flex gap-1">
          <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-widest uppercase font-medium ${CATEGORY_TINT[cat] || CATEGORY_TINT.otro}`}>
            {CATEGORY_LABEL[cat] || "Otro"}
          </span>
        </div>
        {status === "scheduled" && (
          <div className="absolute top-2 right-2 bg-[#0a7f28]/95 text-white text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> Agendada
          </div>
        )}
        {canBeDeleted && (
          <button
            onClick={del}
            className="absolute bottom-2 right-2 bg-white/95 hover:bg-red-500 hover:text-white text-red-500 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
            title="Eliminar"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-[#1A1C1A] line-clamp-2 leading-tight min-h-[2rem]">
          {item.hook || item.analysis?.scene || "Sin análisis aún"}
        </p>
        {item.recommendation?.best_day && (
          <p className="text-[10px] text-[#A69076] mt-2 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            {item.recommendation.best_day} · {item.recommendation.best_time}
          </p>
        )}
      </div>
    </button>
  );
}

function LifestyleDetail({ itemId, onClose, onChange }) {
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(null); // "style" | "copy" | "hook" | "schedule"
  const [palette, setPalette] = useState("cream");
  const [removeBg, setRemoveBg] = useState(false);
  const [context, setContext] = useState("");
  const [customCaption, setCustomCaption] = useState("");
  const [customHook, setCustomHook] = useState("");
  const [hookStyle, setHookStyle] = useState("editorial");
  const [hookPosition, setHookPosition] = useState("bottom");
  const [scheduledFor, setScheduledFor] = useState("");
  const [destination, setDestination] = useState("feed");

  const load = async () => {
    try {
      const r = await api.get(`/lifestyle/${itemId}`);
      setItem(r.data);
      setPalette(r.data.palette || "latte_coral");
      setCustomCaption(r.data.caption || "");
      setCustomHook(r.data.hook || "");
      const rec = r.data.recommendation;
      if (rec?.best_day && rec?.best_time) {
        // build ISO in Mexico City tz (UTC-6): user typed 17:00 MX = 23:00 UTC
        const [hh, mm] = rec.best_time.split(":").map(Number);
        const dt = new Date(`${rec.best_day}T${String(hh).padStart(2,"0")}:${String(mm||0).padStart(2,"0")}:00-06:00`);
        setScheduledFor(dt.toISOString().slice(0, 16));
        setDestination(rec.destination || "feed");
      }
    } catch { toast.error("Error cargando"); onClose(); }
  };
  useEffect(() => { load(); }, [itemId]);

  const doStyle = async () => {
    setBusy("style");
    try {
      await api.post(`/lifestyle/${itemId}/style`, { palette, remove_background: removeBg }, { timeout: 3 * 60 * 1000 });
      toast.success("Estilo aplicado");
      await load(); await onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };
  const doCopy = async () => {
    setBusy("copy");
    try {
      const r = await api.post(`/lifestyle/${itemId}/generate-copy`, { context }, { timeout: 90000 });
      setCustomHook(r.data.hook || "");
      setCustomCaption(r.data.caption || "");
      toast.success("Copy y recomendación generados");
      await load(); await onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };
  const doHook = async () => {
    if (!customHook.trim()) { toast.error("Escribe el gancho"); return; }
    setBusy("hook");
    try {
      await api.post(`/lifestyle/${itemId}/apply-hook`, {
        hook: customHook.trim(), position: hookPosition, style: hookStyle,
      }, { timeout: 60000 });
      toast.success("Gancho aplicado a la imagen");
      await load(); await onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };
  const doSchedule = async () => {
    if (!scheduledFor) { toast.error("Elige fecha y hora"); return; }
    setBusy("schedule");
    try {
      // Convert local input (from datetime-local) to ISO UTC
      const iso = new Date(scheduledFor).toISOString();
      await api.post(`/lifestyle/${itemId}/schedule`, {
        scheduled_for: iso, destination, caption: customCaption,
      });
      toast.success("Agendada correctamente");
      await load(); await onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };

  const [feedPreviewOpen, setFeedPreviewOpen] = useState(false);

  if (!item) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white animate-spin" />
      </div>
    );
  }

  const previewImg = item.final_image_base64 || item.styled_image_base64 || item.image_base64 ||
    (item.image_url && `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`);
  const rec = item.recommendation;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full my-8 grid md:grid-cols-2 gap-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* LEFT — Preview */}
        <div className="bg-[#F4F0EB] p-6 flex flex-col gap-4">
          <div className="bg-[#1A1C1A] rounded-lg overflow-hidden flex items-center justify-center min-h-[320px] max-h-[520px]">
            {previewImg && (
              <img src={previewImg} alt="preview" className="max-w-full max-h-[520px] object-contain"
                   data-testid="lifestyle-preview-img" />
            )}
          </div>
          <button
            type="button"
            onClick={() => setFeedPreviewOpen(true)}
            className="w-full text-[10px] tracking-widest uppercase text-[#D97757] border border-[#D97757] rounded-full py-2 hover:bg-[#FBE9E1] transition inline-flex items-center justify-center gap-1"
            data-testid="lifestyle-preview-ig"
          >
            <ChevronRight className="w-3 h-3" /> Ver cómo se verá en Instagram
          </button>
          <div className="text-[10px] tracking-widest uppercase text-[#A69076]">
            {item.status === "uploaded" ? "Original — aún sin estilo" :
             item.status === "styled" ? "Con estilo · sin gancho" :
             item.status === "recommended" ? "Con copy generado" :
             item.status === "scheduled" ? "Agendada" : item.status}
          </div>
          {item.analysis?.scene && (
            <div className="bg-white rounded-lg p-3 text-xs text-[#5C5E5C] leading-relaxed">
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Análisis IA</div>
              {item.analysis.scene}
              {item.analysis.publishable === false && (
                <p className="mt-2 text-[#B54B34] text-[11px]">
                  ⚠ La IA sugiere NO publicar: {item.analysis.quality_notes}
                </p>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Controls */}
        <div className="p-6 space-y-5 overflow-y-auto max-h-[85vh]">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#A69076]">Editar lifestyle</div>
              <h3 className="font-display text-2xl text-[#1A1C1A]">
                {CATEGORY_LABEL[item.analysis?.category] || "Foto"}
              </h3>
            </div>
            <button onClick={onClose} className="text-[#5C5E5C] hover:text-[#1A1C1A] text-2xl leading-none">×</button>
          </div>

          {/* Step 1 — Style */}
          <Section title="1. Estilo editorial" icon={<Palette className="w-3 h-3" />} done={!!item.styled_image_base64}>
            <div className="grid grid-cols-3 gap-1.5">
              {PALETTES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPalette(p.id)}
                  className={`p-2 rounded-lg border text-[9px] tracking-widest uppercase flex items-center gap-1.5 ${palette === p.id ? "border-[#D97757] bg-[#FBE9E1]" : "border-[#E6E2DC] hover:border-[#A69076]"}`}
                  data-testid={`palette-${p.id}`}
                >
                  <span className="flex -space-x-1">
                    {p.swatch.map((c, i) => <span key={i} className="w-3 h-3 rounded-full border border-white" style={{background: c}} />)}
                  </span>
                  <span className="truncate">{p.label}</span>
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-[#5C5E5C] cursor-pointer mt-2">
              <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} data-testid="remove-bg-toggle" />
              Reemplazar fondo con color de paleta (para fotos con distracciones)
            </label>
            <Button onClick={doStyle} disabled={!!busy} size="sm" className="mt-2 w-full bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs" data-testid="lifestyle-apply-style">
              {busy === "style" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {item.styled_image_base64 ? "Re-aplicar estilo" : "Aplicar estilo"}
            </Button>
          </Section>

          {/* Step 2 — Copy */}
          <Section title="2. Hook + caption + recomendación IA" icon={<Wand2 className="w-3 h-3" />} done={!!item.caption}>
            <Textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Contexto opcional (ej: 'café en la CDMX antes de un curso')"
              rows={2}
              className="text-xs"
            />
            <Button onClick={doCopy} disabled={!!busy} size="sm" className="w-full bg-[#1A1C1A] hover:bg-[#D97757] text-white text-xs" data-testid="lifestyle-generate-copy">
              {busy === "copy" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
              {item.caption ? "Regenerar copy + plan" : "Generar copy + plan"}
            </Button>

            {rec && (
              <div className={`rounded-lg p-3 border ${rec.should_publish ? "bg-[#E7F3EA] border-[#0a7f28]/30" : "bg-[#FBE9E1] border-[#D97757]/30"}`} data-testid="ai-recommendation">
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase mb-2">
                  {rec.should_publish
                    ? <><CheckCircle2 className="w-3 h-3 text-[#0a7f28]" /><span className="text-[#0a7f28] font-semibold">Recomendada</span></>
                    : <><XCircle className="w-3 h-3 text-[#B54B34]" /><span className="text-[#B54B34] font-semibold">No publicar</span></>
                  }
                  <span className="text-[#5C5E5C]">· conf {Math.round((rec.confidence || 0) * 100)}%</span>
                </div>
                <p className="text-xs text-[#1A1C1A] leading-relaxed">{rec.reason}</p>
                {rec.should_publish && (
                  <div className="mt-2 grid grid-cols-3 gap-2 text-[10px]">
                    <div><span className="text-[#A69076] tracking-widest uppercase block text-[9px]">Destino</span><span className="text-[#1A1C1A] font-medium">{rec.destination}</span></div>
                    <div><span className="text-[#A69076] tracking-widest uppercase block text-[9px]">Día</span><span className="text-[#1A1C1A] font-medium">{rec.best_day}</span></div>
                    <div><span className="text-[#A69076] tracking-widest uppercase block text-[9px]">Hora MX</span><span className="text-[#1A1C1A] font-medium">{rec.best_time}</span></div>
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* Step 3 — Hook overlay */}
          <Section title="3. Quemar el gancho en la imagen" icon={<Sparkles className="w-3 h-3" />} done={!!item.final_image_base64 && item.final_image_base64 !== item.styled_image_base64}>
            <Textarea
              value={customHook}
              onChange={(e) => setCustomHook(e.target.value.slice(0, 140))}
              rows={2}
              placeholder="Gancho editorial máx 12 palabras"
              className="text-xs"
              data-testid="lifestyle-hook-input"
            />
            <div className="flex items-center gap-1.5">
              {["editorial", "bold", "minimal"].map((s) => (
                <button
                  key={s}
                  onClick={() => setHookStyle(s)}
                  className={`text-[9px] tracking-widest uppercase px-2 py-1 rounded ${hookStyle === s ? "bg-[#1A1C1A] text-white" : "bg-[#F4F0EB] text-[#1A1C1A]"}`}
                >{s}</button>
              ))}
              <span className="text-[9px] text-[#5C5E5C] ml-2">Posición:</span>
              {["top", "top-left", "bottom", "bottom-left"].map((p) => (
                <button
                  key={p}
                  onClick={() => setHookPosition(p)}
                  className={`text-[9px] tracking-widest uppercase px-1.5 py-1 rounded ${hookPosition === p ? "bg-[#D97757] text-white" : "bg-[#F4F0EB] text-[#1A1C1A]"}`}
                >{p.replace("-left", "←")}</button>
              ))}
            </div>
            <Button onClick={doHook} disabled={!!busy} size="sm" className="w-full bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs" data-testid="lifestyle-apply-hook">
              {busy === "hook" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Aplicar gancho
            </Button>
          </Section>

          {/* Step 4 — Schedule */}
          <Section title="4. Agendar" icon={<CalendarClock className="w-3 h-3" />} done={item.status === "scheduled"}>
            {item.status === "scheduled" ? (
              <p className="text-[11px] text-[#0a7f28] flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Ya está agendada. Se publicará el {new Date(item.scheduled_for).toLocaleString("es-MX")}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="text-xs"
                    data-testid="lifestyle-schedule-datetime"
                  />
                  <select
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="text-xs border border-[#E6E2DC] rounded-md px-2 py-1"
                  >
                    <option value="feed">Feed</option>
                    <option value="stories">Stories</option>
                    <option value="reel">Reel</option>
                  </select>
                </div>
                <Textarea
                  value={customCaption}
                  onChange={(e) => setCustomCaption(e.target.value)}
                  rows={4}
                  placeholder="Caption final (edita si quieres)"
                  className="text-xs"
                />
                <Button onClick={doSchedule} disabled={!!busy} size="sm" className="w-full bg-[#0a7f28] hover:bg-[#1A1C1A] text-white text-xs" data-testid="lifestyle-schedule-btn">
                  {busy === "schedule" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CalendarClock className="w-3 h-3 mr-1" />}
                  Agendar publicación
                </Button>
              </>
            )}
          </Section>
        </div>
      </div>

      {/* Feed preview modal — shows exactly how the post will look on IG grid & feed */}
      {feedPreviewOpen && (
        <div className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4 overflow-y-auto"
             onClick={() => setFeedPreviewOpen(false)} data-testid="feed-preview-modal">
          <div className="max-w-4xl w-full my-8 space-y-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-1">Vista previa Instagram</div>
              <h3 className="font-display text-2xl text-white">Así se verá cuando publiques</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* GRID thumbnail: 1:1 crop centered */}
              <div className="space-y-2">
                <div className="text-[10px] tracking-widest uppercase text-white/70 flex items-center justify-between">
                  <span>Grid del perfil (1:1)</span>
                  <span className="text-white/40 normal-case tracking-normal">Miniatura</span>
                </div>
                <div className="bg-white rounded-lg p-2">
                  <div className="grid grid-cols-3 gap-0.5 aspect-square">
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square overflow-hidden ring-2 ring-[#D97757]">
                      {previewImg && <img src={previewImg} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square bg-[#EEE]"></div>
                    <div className="aspect-square bg-[#EEE]"></div>
                  </div>
                </div>
                <p className="text-[11px] text-white/60 leading-relaxed">
                  Instagram recorta al centro 1:1 en la miniatura del grid. Los bordes de arriba/abajo se pierden si tu foto es vertical.
                </p>
              </div>

              {/* FEED post: 4:5 vertical */}
              <div className="space-y-2">
                <div className="text-[10px] tracking-widest uppercase text-white/70 flex items-center justify-between">
                  <span>Post en el feed (4:5)</span>
                  <span className="text-white/40 normal-case tracking-normal">Como lo ven tus pacientes</span>
                </div>
                <div className="bg-white rounded-lg overflow-hidden">
                  <div className="p-3 flex items-center gap-2 border-b border-[#EEE]">
                    <div className="w-7 h-7 rounded-full bg-[#D97757]"></div>
                    <div className="text-xs font-medium text-[#1A1C1A]">dra.taniamendoza</div>
                    <div className="text-[10px] text-[#666] ml-auto">Pachuca, Hidalgo</div>
                  </div>
                  <div className="bg-[#000] flex items-center justify-center" style={{aspectRatio: "4/5"}}>
                    {previewImg && <img src={previewImg} alt="" className="max-w-full max-h-full object-contain" />}
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="flex gap-3 text-[#1A1C1A]">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    </div>
                    <p className="text-xs text-[#1A1C1A] leading-relaxed line-clamp-3">
                      <span className="font-semibold">dra.taniamendoza</span>{" "}
                      {(customCaption || item.caption || "").split("\n")[0]}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-white/60 leading-relaxed">
                  El feed muestra la foto completa (hasta 4:5). Si tu foto es más alta (9:16), aparecen bandas negras arriba/abajo.
                </p>
              </div>
            </div>
            <div className="text-center">
              <button
                onClick={() => setFeedPreviewOpen(false)}
                className="text-[10px] tracking-widest uppercase text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-6 py-2"
              >Cerrar preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, done, children }) {
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${done ? "border-[#0a7f28]/30 bg-[#E7F3EA]/30" : "border-[#E6E2DC]"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-[#1A1C1A] font-medium">
          {icon} {title}
        </div>
        {done && <CheckCircle2 className="w-3.5 h-3.5 text-[#0a7f28]" />}
      </div>
      {children}
    </div>
  );
}

function RebalanceButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    if (!confirm("Barrer el calendario y asignar día/hora a todas las fotos pendientes?")) return;
    setBusy(true);
    try {
      const r = await api.post("/lifestyle/rebalance-calendar", {});
      const n = r.data?.assigned || 0;
      toast.success(n > 0 ? `${n} fotos con día/hora sugerida` : "No hay pendientes que asignar");
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };
  return (
    <Button onClick={run} disabled={busy} size="sm"
            className="bg-[#D97757] hover:bg-[#1A1C1A] text-white text-xs"
            data-testid="lifestyle-rebalance-btn">
      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CalendarClock className="w-3 h-3 mr-1" />}
      Barrer calendario
    </Button>
  );
}
